import AlertTriangleIcon from '@assets/icons/alert-triangle.svg';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { Alert, Level } from '@/components/alert';
import { AvailabilityMap, ItemListOption } from '@/contexts/item-context-types';
import { createStyles } from '@/contexts/theme-context';
import { DeleteActionView } from '@/components/delete-action-view';
import { EditFulfillmentDateButton } from './edit-fulfillment-date-button';
import { EditOrderItemNoteParams } from '@/navigators/types';
import { FONTS, LIST_MARGIN_HORIZONTAL, TYPOGRAPHY } from '@/styles';
import { formatCurrency } from '@/utils/money';
import { formatSummaryDate } from '@/utils/date';
import { FulfillmentName, NewOrder_OrderFieldsFragment } from '@/types';
import { GESTURE_RESPONSE_DISTANCE } from '@/hooks/use-default-stack-screen-options';
import { gql } from '@apollo/client';
import {
  ItemWithSubstitute,
  OriginalVariantPack,
} from './substitution-item-wrapper';
import { KeyboardAwareProps } from '@/components/with-keyboard-aware';
import {
  KeyboardAwareRecyclerList,
  ListSection,
  Props as RecyclerListProps,
  RecyclerListRef,
} from '@/components/recycler-list';
import { ListEmptyState } from '@/components/list-empty-state';
import { Props as ListItemProps } from '@/components/list-item';
import { ORDER_ITEM_LIST_FRAGMENT } from '@/components/order-item-list';
import { OrderItemSwipeActions } from './order-item-swipe-actions';
import { OrderSummaryItemPromotions } from './order-summary-list-item';
import {
  OrderSummaryListItemType,
  OrderSummaryMainContent,
} from './order-summary-main-content';
import { Platform, Text, TouchableOpacity, View } from 'react-native';
import { translateFulfillmentType } from '@domain/fulfillment-settings/utils/translate-fulfillment-type';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { useCanWriteLineItemRequest } from '@/hooks/dynamic-config/use-can-write-line-item-request';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useSupplierOrderAvailability } from '@/hooks/use-supplier-order-availability';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

const SECTION_HEADER_HEIGHT = 32;
const SECTION_HEADER_ALERT_HEIGHT = 32;
const SECTION_HEADER_ALERT_MARGIN = 16;
export const SECTION_BORDER_RADIUS = 16;
export const ORDER_SUMMARY_PROMOTION_FIELDS_FRAGMENT = gql`
  fragment OrderSummary_PromotionFields on promotions {
    background_color_start
    background_color_end
    marketing_text
    uuid
  }
`;

export const ORDER_SUMMARY_ITEM_LIST_FIELDS_FRAGMENT = gql`
  fragment OrderSummary_ItemListFields on orders_items {
    ...OrderItemListFields
    earliest_fulfillment_at
    order_item_promotion_rels {
      discount_in_micros
      promotion {
        ...OrderSummary_PromotionFields
        discount_type
        expires_at
        tagline
      }
      uuid
    }
  }

  ${ORDER_ITEM_LIST_FRAGMENT}
  ${ORDER_SUMMARY_PROMOTION_FIELDS_FRAGMENT}
`;

export interface SectionMetadataMap {
  [sectionKey: string]: {
    baseAmountInMicros: number;
    discountedAmountInMicros: number;
    hasNoSearchResults: boolean;
    headerText: string;
    orderMinimumError: string | null;
    showFulfillmentButton: boolean;
  };
}

interface Props {
  availableQuantityMap: AvailabilityMap;
  focusedIndex: number | null;
  fulfillmentButtonElement?: React.ReactNode;
  fulfillmentType: FulfillmentName | null;
  itemPromotionsMap?: { [orderItemUUID: string]: OrderSummaryItemPromotions };
  keyboardAwareProps: KeyboardAwareProps;
  ListFooterComponent?: RecyclerListProps<ItemListOption>['ListFooterComponent'];
  ListHeaderComponent?: RecyclerListProps<ItemListOption>['ListHeaderComponent'];
  listRef?: React.Ref<RecyclerListRef<OrderSummaryListItemType>>;
  loyaltyPointsMap?: { [orderItemUUID: string]: number };
  onChooseSubstitutionPress: (
    itemWithSubstitute: ItemWithSubstitute<ItemListOption>,
  ) => Promise<void>;
  onClearSubstitutionPress: (
    originalVariantPack: OriginalVariantPack,
    substituteVariantPackUUID?: string,
  ) => Promise<void>;
  onDoNotSubstitutePress: (
    originalVariantPack: OriginalVariantPack,
  ) => Promise<void>;
  onEditDatePress: () => void;
  onEditOrderItemNote: (params: EditOrderItemNoteParams) => void;
  onOrderItemNotesSaved?: (order: NewOrder_OrderFieldsFragment) => void;
  onSalesRepPriceUpdated: () => void;
  onVisible: () => void;
  orderUUID: string;
  quantityUpdatesPending: boolean;
  removeItems: (variantPackUUIDs: string[]) => Promise<void>;
  resetDisabled: boolean;
  searchTerm: string;
  sectionMetadataMap: SectionMetadataMap;
  sections: ListSection<OrderSummaryListItemType>[];
  skipInteractionCheck?: boolean;
  smartOrderUUID: string | null;
  updateQuantity: UpdateQuantityFn;
}

export const OrderSummaryList: React.FC<Props> = ({
  availableQuantityMap,
  focusedIndex,
  fulfillmentButtonElement,
  fulfillmentType,
  itemPromotionsMap,
  loyaltyPointsMap,
  keyboardAwareProps,
  ListFooterComponent,
  ListHeaderComponent,
  listRef,
  onChooseSubstitutionPress,
  onClearSubstitutionPress,
  onDoNotSubstitutePress,
  onEditDatePress,
  onEditOrderItemNote,
  onOrderItemNotesSaved,
  onSalesRepPriceUpdated,
  onVisible,
  orderUUID,
  quantityUpdatesPending,
  removeItems,
  resetDisabled,
  searchTerm,
  sectionMetadataMap,
  sections,
  skipInteractionCheck,
  smartOrderUUID,
  updateQuantity,
}) => {
  const [styles, theme] = useStyles();
  const { currencyCode } = useVariantPackInfoContext();
  const orderAvailability = useSupplierOrderAvailability();
  const fulfillmentDisplayName = orderAvailability.fulfillmentDisplayName;
  const fulfillmentDisclaimer = orderAvailability.fulfillmentDisclaimer;
  const orderSummarySectionsEnabled = useDynamicConfigValue(
    'order_summary_sections_enabled',
  );
  const isOrderNoteEnabled = useSupplierDynamicConfigValue(
    'line_item_notes_config',
  )?.enabled;

  const canWriteLineItemRequest = useCanWriteLineItemRequest();

  const lastOrderItemsInSections = useMemo<Set<string>>(() => {
    if (!orderSummarySectionsEnabled) {
      return new Set();
    }
    const lastItemSet = new Set<string>();
    for (const section of sections) {
      if (section.data.length > 0) {
        const lastItem = section.data[section.data.length - 1];
        lastItemSet.add(String(lastItem.value));
      }
    }
    return lastItemSet;
  }, [sections, orderSummarySectionsEnabled]);

  const listItemProps: Partial<ListItemProps<OrderSummaryListItemType>> =
    useMemo(() => {
      return {
        listItemStyle: (itemOption) => {
          return [
            itemOption.metadata?.substitutionVariantPack
              ? styles.listItemStyleSubstitution
              : styles.listItemStyle,
            lastOrderItemsInSections.has(String(itemOption.value))
              ? styles.listItemStyleSectionEnd
              : null,
          ];
        },
        renderMainContent: (itemOption) => {
          return (
            <OrderSummaryMainContent
              availableQuantityMap={availableQuantityMap}
              isFocused={itemOption.flatIndex === focusedIndex}
              itemPromotionsMap={itemPromotionsMap}
              listItem={itemOption}
              loyaltyPointsMap={loyaltyPointsMap}
              onChooseSubstitutionPress={onChooseSubstitutionPress}
              onClearSubstitutionPress={onClearSubstitutionPress}
              onDoNotSubstitutePress={onDoNotSubstitutePress}
              onEditNotes={() => {
                const item = itemOption.metadata?.orderItems[0]?.item;
                if (!item?.metadata?.variantPackUUID) {
                  return;
                }
                onEditOrderItemNote({
                  initialNotes: item.metadata?.notes ?? '',
                  onNotesSaved: onOrderItemNotesSaved,
                  variantPackUUID: item.metadata.variantPackUUID,
                });
              }}
              onSalesRepPriceUpdated={onSalesRepPriceUpdated}
              orderUUID={orderUUID}
              quantityUpdatesPending={quantityUpdatesPending}
              removeItems={removeItems}
              resetDisabled={resetDisabled}
              updateQuantity={updateQuantity}
            />
          );
        },
        swipeableProps:
          Platform.OS === 'web'
            ? undefined
            : isOrderNoteEnabled
              ? {
                  // move left hitslop to enable swipe to navigate back
                  hitSlop: { left: -GESTURE_RESPONSE_DISTANCE },
                  renderRightActions: (option, progress, _, swipeableRef) => {
                    const topLevelMetadata = option.metadata;
                    if (!topLevelMetadata) {
                      return null;
                    }

                    const variantPackUUIDs = topLevelMetadata.orderItems.reduce<
                      string[]
                    >((acc, orderItemData) => {
                      const vpUUID =
                        orderItemData.item.metadata?.variantPackUUID;
                      if (vpUUID) {
                        acc.push(vpUUID);
                      }
                      return acc;
                    }, []);

                    return (
                      <OrderItemSwipeActions
                        canEditNote={canWriteLineItemRequest}
                        onEditNote={() => {
                          const item = topLevelMetadata.orderItems[0]?.item;
                          swipeableRef?.instantClose();
                          if (!item?.metadata?.variantPackUUID) {
                            return;
                          }
                          onEditOrderItemNote({
                            initialNotes: item.metadata?.notes ?? '',
                            onNotesSaved: onOrderItemNotesSaved,
                            variantPackUUID: item.metadata.variantPackUUID,
                          });
                        }}
                        onRemove={async () => {
                          if (!variantPackUUIDs.length) {
                            swipeableRef?.instantClose();
                            return;
                          }
                          await removeItems(variantPackUUIDs);
                          swipeableRef?.instantClose();
                        }}
                        progress={progress}
                        trackingProperties={{
                          orderUUID,
                          variantPackUUIDs,
                        }}
                      />
                    );
                  },
                }
              : {
                  // move left hitslop to enable swipe to navigate back
                  hitSlop: { left: -GESTURE_RESPONSE_DISTANCE },
                  onSwipeableRightWillOpen: async (options, swipeableRef) => {
                    const topLevelMetadata = options.metadata;
                    const option = topLevelMetadata?.orderItems[0]?.item;
                    if (!option) {
                      return;
                    }
                    const variantPackUUIDs = (
                      options.metadata?.orderItems || []
                    ).reduce<string[]>((acc, oiData) => {
                      const vpUUID = oiData.item.metadata?.variantPackUUID;
                      if (vpUUID) {
                        acc.push(vpUUID);
                      }
                      return acc;
                    }, []);
                    await removeItems(variantPackUUIDs);
                    swipeableRef?.instantClose();
                  },
                  renderRightActions: (_option, progress, drag) => {
                    return <DeleteActionView drag={drag} progress={progress} />;
                  },
                },
      };
    }, [
      availableQuantityMap,
      focusedIndex,
      itemPromotionsMap,
      loyaltyPointsMap,
      canWriteLineItemRequest,
      isOrderNoteEnabled,
      lastOrderItemsInSections,
      onChooseSubstitutionPress,
      onClearSubstitutionPress,
      onDoNotSubstitutePress,
      onEditOrderItemNote,
      onOrderItemNotesSaved,
      onSalesRepPriceUpdated,
      orderUUID,
      quantityUpdatesPending,
      removeItems,
      resetDisabled,
      styles,
      updateQuantity,
    ]);

  return (
    <>
      <KeyboardAwareRecyclerList
        estimatedItemSize={160}
        keyboardShouldPersistTaps="handled"
        ListFooterComponent={ListFooterComponent}
        ListHeaderComponent={ListHeaderComponent}
        listItemProps={listItemProps}
        listItemSeparatorProps={{
          style: styles.listItemSeparator,
        }}
        listRef={listRef}
        onVisible={onVisible}
        renderSectionHeader={(item, renderTarget, index) => {
          const alertMessage =
            sectionMetadataMap[item.title]?.orderMinimumError;
          const hasNoSectionSearchResults =
            sectionMetadataMap[item.title]?.hasNoSearchResults;

          if (!orderSummarySectionsEnabled) {
            return (
              <>
                <View style={styles.listSectionHeader}>
                  <Text
                    style={[TYPOGRAPHY.small, styles.listSectionHeaderText]}
                  >
                    {formatSummaryDate(item.title)}
                  </Text>
                </View>
                {alertMessage ? (
                  <Alert
                    Icon={AlertTriangleIcon}
                    iconProps={{ fill: theme.colors.warningContent }}
                    level={Level.WARNING}
                    style={styles.listSectionHeaderAlert}
                  >
                    {alertMessage}
                  </Alert>
                ) : null}
              </>
            );
          }

          const orderTotalInMicros =
            sectionMetadataMap[item.title]?.discountedAmountInMicros;
          const formattedPrice = formatCurrency(
            orderTotalInMicros,
            currencyCode,
          );
          const headerText = sectionMetadataMap[item.title]?.headerText;
          const showFulfillmentButton =
            sectionMetadataMap[item.title]?.showFulfillmentButton;

          const fulfillmentDisplayString = translateFulfillmentType(
            fulfillmentType || FulfillmentName.Delivery,
            'fulfillment_for',
            fulfillmentDisplayName,
            {
              date: formatSummaryDate(item.title),
            },
          );

          if (renderTarget === 'Cell') {
            return (
              <View
                style={[
                  styles.cellHeaderBase,
                  alertMessage ? null : styles.cellHeaderNoAlert,
                ]}
              >
                <View style={styles.cellHeaderContent}>
                  <View style={[styles.cellHeaderRow, styles.cellHeaderRowTop]}>
                    {showFulfillmentButton ? (
                      fulfillmentButtonElement ? (
                        <View style={styles.editDateButtonContainer}>
                          {fulfillmentButtonElement}
                        </View>
                      ) : (
                        <EditFulfillmentDateButton
                          containerStyle={styles.editDateButtonContainer}
                          date={item.title}
                          fulfillmentType={fulfillmentType}
                          onPress={onEditDatePress}
                          testID="edit-fulfillment-date-button"
                          trackingData={{
                            name: 'order_summary_header.edit_date_button',
                            properties: {
                              orderUUID,
                              smartOrderUUID,
                            },
                          }}
                        />
                      )
                    ) : (
                      <Text style={[TYPOGRAPHY.medium, FONTS.primarySemiBold]}>
                        {fulfillmentDisplayString}
                      </Text>
                    )}
                  </View>
                  {fulfillmentDisclaimer ? (
                    <Text style={TYPOGRAPHY.small}>
                      {fulfillmentDisclaimer}
                    </Text>
                  ) : null}
                  {searchTerm ? null : (
                    <View
                      style={[styles.cellHeaderRow, styles.cellHeaderRowBottom]}
                    >
                      <Text
                        numberOfLines={2}
                        style={[TYPOGRAPHY.small, styles.cellHeaderWrapLeft]}
                      >
                        {headerText}
                      </Text>
                      <Text
                        style={[
                          TYPOGRAPHY.small,
                          FONTS.primarySemiBold,
                          styles.cellHeaderWrapRight,
                        ]}
                      >
                        {formattedPrice}
                      </Text>
                    </View>
                  )}
                </View>
                {alertMessage ? (
                  <View style={styles.cellHeaderAlertMessage}>
                    <View style={styles.alertMessageBorder} />
                    <View style={styles.alertMessageContent}>
                      <AlertTriangleIcon fill={theme.colors.warningContent} />
                      <Text
                        style={[
                          TYPOGRAPHY.small,
                          styles.cellHeaderAlertMessageText,
                        ]}
                      >
                        {alertMessage}
                      </Text>
                    </View>
                    <View style={styles.alertMessageBorder} />
                  </View>
                ) : null}
                {hasNoSectionSearchResults ? (
                  <ListEmptyState
                    text={i18n.t('no_results_for', {
                      searchTerm,
                    })}
                  />
                ) : null}
              </View>
            );
          }

          return (
            <TouchableOpacity
              activeOpacity={0.9}
              onPress={() => {
                if (typeof listRef === 'object') {
                  listRef?.current?.scrollToIndex({
                    animated: true,
                    index,
                  });
                }
              }}
              style={styles.stickyHeaderBase}
            >
              <View
                style={[
                  styles.stickyHeaderContent,
                  styles.stickyHeaderContentLeft,
                ]}
              >
                <Text
                  style={[
                    TYPOGRAPHY.small,
                    FONTS.primarySemiBold,
                    styles.stickyHeaderDateText,
                  ]}
                >
                  {fulfillmentDisplayString}
                </Text>
              </View>
              <View
                style={[
                  styles.stickyHeaderContent,
                  styles.stickyHeaderContentRight,
                ]}
              >
                {alertMessage ? (
                  <AlertTriangleIcon fill={theme.colors.warningContent} />
                ) : null}
                {searchTerm ? null : (
                  <>
                    <Text
                      numberOfLines={1}
                      style={[TYPOGRAPHY.small, styles.stickyHeaderDetailText]}
                    >
                      {headerText}
                    </Text>
                    <Text style={TYPOGRAPHY.small}>·</Text>
                    <Text style={[TYPOGRAPHY.small, FONTS.primarySemiBold]}>
                      {formattedPrice}
                    </Text>
                  </>
                )}
              </View>
            </TouchableOpacity>
          );
        }}
        scrollEventThrottle={100}
        sections={sections}
        shouldRenderSeparator={() => false}
        skipInteractionCheck={skipInteractionCheck}
        testID="order-summary-item-list"
        {...(orderSummarySectionsEnabled ? {} : { stickyHeaderIndices: [] })}
        {...keyboardAwareProps}
      />
    </>
  );
};

const useStyles = createStyles((theme) => ({
  alertMessageBorder: {
    backgroundColor: theme.colors.stroke,
    flex: 1,
    height: 1,
  },
  alertMessageContent: {
    alignItems: 'center',
    backgroundColor: theme.colors.cautionHighlight,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    maxWidth: '80%',
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  cellHeaderAlertMessage: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
  cellHeaderAlertMessageText: {
    marginLeft: 8,
  },
  cellHeaderBase: {
    backgroundColor: theme.colors.white,
    borderTopLeftRadius: SECTION_BORDER_RADIUS,
    borderTopRightRadius: SECTION_BORDER_RADIUS,
    marginTop: 16,
    paddingTop: 16,
  },
  cellHeaderContent: {
    gap: 8,
    paddingHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  cellHeaderNoAlert: {
    borderBottomColor: theme.colors.stroke,
    borderBottomWidth: 1,
    paddingBottom: 14,
  },
  cellHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  cellHeaderRowBottom: {
    alignItems: 'flex-start',
    gap: 8,
  },
  cellHeaderRowTop: {
    alignItems: 'center',
  },
  cellHeaderWrapLeft: {
    flexShrink: 1,
  },
  cellHeaderWrapRight: {
    flexShrink: 0,
  },
  editDateButtonContainer: {
    flex: 1,
    marginBottom: 8,
  },
  listItemSeparator: {
    marginHorizontal: 16,
  },
  listItemStyle: {
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  listItemStyleSectionEnd: {
    borderBottomLeftRadius: SECTION_BORDER_RADIUS,
    borderBottomRightRadius: SECTION_BORDER_RADIUS,
  },
  listItemStyleSubstitution: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  listSectionHeader: {
    backgroundColor: theme.colors.background,
    height: SECTION_HEADER_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  listSectionHeaderAlert: {
    alignItems: 'center',
    flexDirection: 'row',
    height: SECTION_HEADER_ALERT_HEIGHT,
    marginHorizontal: LIST_MARGIN_HORIZONTAL,
    marginTop: SECTION_HEADER_ALERT_MARGIN,
  },
  listSectionHeaderText: {
    ...FONTS.primarySemiBold,
    textTransform: 'capitalize',
  },
  stickyHeaderBase: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderBottomColor: theme.colors.stroke,
    borderBottomWidth: 1,
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
    paddingHorizontal: LIST_MARGIN_HORIZONTAL,
    paddingVertical: 8,
  },
  stickyHeaderContent: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  stickyHeaderContentLeft: {
    flexShrink: 0,
  },
  stickyHeaderContentRight: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  stickyHeaderDateText: {
    flexShrink: 0,
  },
  stickyHeaderDetailText: {
    flexShrink: 1,
  },
}));
